import { Constants, CYBER_CONSTANT } from 'constants';

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Grid,
  Checkbox,
  TextField,
  Button,
  Skeleton,
} from '@genie-ui/react';
import { useTimeoutReset } from '@genie-ui/react/hooks';
import { useSelector } from 'react-redux';

import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MTTC from 'assets/images/MTTC.png';
import MTTR from 'assets/images/MTTR.png';
import {
  postKpiDetail,
  getPeerDiscussion,
} from 'components/pages/cyber/api';
import TrendChart from 'components/atoms/trendchart';
import CarousalComponent from 'components/atoms/carousalcomponent';
import {
  inputValue,
  debounce,
  getDisplayDate,
  convertToLastDate,
} from 'utils';
import SnackbarComponent from 'components/atoms/snackbarcomponent';

import './DrawerComponent.scss';

function DrawerComponent({
  kpiItem,
  handleClose,
  handleError,
  handleSave: handleSaveFromProps,
  benchmarkForKpi: benchmarkData,
  minimalBenchmarkData,
  actisData,
  isVersionTwoEnabled,
  isLoadingBenchmark,
  shouldShowCyberLegend,
  kpiRecommendedResources,
  showResourcesLoader,
  dashboardId,
  drawerStatus,
}) {
  const {
    cohortSelection,
  } = useSelector((state) => state.auth);

  // handle current and target values
  const [curValue, setCurValue] = useState(
    kpiItem?.curValue || kpiItem?.curValue === 0
      ? String(kpiItem?.curValue)
      : undefined,
  );
  const [tarValue, setTarValue] = useState(
    kpiItem?.tarValue || kpiItem?.tarValue === 0
      ? String(kpiItem?.tarValue)
      : undefined,
  );
  const [curEstimateCheck, setCurEstimateCheck] = useState(
    !!kpiItem?.curEstimateCheck,
  );
  const [hasError, setHasError] = useState(false);
  const [hasFallbackError, setHasFallbackError] = useState(false);
  const [hasNonZeroError, setHasNonZeroError] = useState(false);
  const [hasInvalidHours, setHasInvalidHours] = useState(false);
  const [hasTooHighTargetValue, setHasTooHighTargetValue] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [shouldShowSuccess, setShouldShowSuccess] = useState(false);
  const [isShowingFullContext, setIsShowingFullContext] = useState(false);
  const [kpiPeerDiscussion, setKpiPeerDiscussion] = useState([]);
  const [wentWrong, setWentWrong] = useState(false);
  const [limitExceeded, setLimitExceeded] = useState(false);

  useEffect(() => {
    if (drawerStatus) {
      getPeerDiscussion(dashboardId, kpiItem?.kpiId).then(
        (localPeer) => {
          if (localPeer?.body) {
            setKpiPeerDiscussion(localPeer?.body?.peerDiscussions);
          }
        },
      );
    }
  }, [drawerStatus]);

  useTimeoutReset(hasNonZeroError, setHasNonZeroError, 3000);
  useTimeoutReset(hasError, setHasError, 3000);
  useTimeoutReset(hasInvalidHours, setHasInvalidHours, 3000);
  useTimeoutReset(hasTooHighTargetValue, setHasTooHighTargetValue, 3000);

  const handleSave = useRef(handleSaveFromProps).current;

  const submitData = (kpiId, _curEstimateCheck, _curValue, _tarValue) => {
    const postBenchmarkingData = {
      kpiId,
      curEstimateCheck: _curEstimateCheck,
      ...(_curValue?.length && {
        curValue: Number(_curValue),
      }),
      ...(_tarValue?.length && {
        tarValue: Number(_tarValue),
      }),
    };

    handleSave({
      tarValue: _tarValue,
      curValue: _curValue,
      curEstimateCheck: _curEstimateCheck,
    });

    postKpiDetail(postBenchmarkingData, dashboardId).then(
      (data) => {
        if (!data?.body || data?.body?.status === Constants.SNACKBAR.ERROR) {
          setWentWrong(true);
        } else {
          setHasError(false);
          setHasFallbackError(false);
          setHasNonZeroError(false);
          setShouldShowSuccess(true);
        }
      },
      () => {
        handleError();
      },
    );
  };

  const debouncedSubmitData = useCallback(debounce(submitData, 1000), []);

  const inputData = (type, unit, header) => (e) => {
    const value = e?.target?.value;
    const nonZeroError = !!(value === '0' && Constants.NON_ZERO_KPI_IDS.includes(kpiItem.kpiId));
    setHasNonZeroError(nonZeroError);

    const valueLength = value?.split('.')[0].length;
    const isPrefilledValue = ((type === CYBER_CONSTANT.LABEL.ISCURRENT)
      ? (curValue?.split('.')[0].length > CYBER_CONSTANT.INPUT_LIMIT)
      : (tarValue?.split('.')[0].length > CYBER_CONSTANT.INPUT_LIMIT));

    // Updated regex to check if the value is numeric and does not contain any alphabetic characters
    const isNumeric = /^[0-9]*\.?[0-9]+$/.test(Number(value));
    if (!isNumeric && type !== CYBER_CONSTANT.LABEL.ISCHECK && value) {
      setHasError(true);
      return;
    }

    if (type === CYBER_CONSTANT.LABEL.ISCURRENT) {
      if (nonZeroError) {
        return;
      }
      if (!isPrefilledValue && valueLength > CYBER_CONSTANT.INPUT_LIMIT) {
        setLimitExceeded(true);
        return;
      }
      setHasChanged(true);
      setCurValue(
        inputValue(
          value,
          curValue,
          CYBER_CONSTANT.LABEL.ISCURRENT,
          unit,
          type,
        ),
      );
      setHasError(!!(value < 0));
      setHasFallbackError(!!(value > 100 && unit === '%'));
      setHasInvalidHours(
        !!(value >= 250 && header === CYBER_CONSTANT.LABEL.ACCESS_KPI),
      );
    } else if (type === CYBER_CONSTANT.LABEL.ISTARGET) {
      if (nonZeroError) {
        return;
      }
      if (!isPrefilledValue && valueLength > CYBER_CONSTANT.INPUT_LIMIT) {
        setLimitExceeded(true);
        return;
      }
      setHasChanged(true);
      setTarValue(
        inputValue(
          value,
          tarValue,
          CYBER_CONSTANT.LABEL.ISTARGET,
          unit,
          type,
        ),
      );
      setHasError(!!(value < 0));
      setHasFallbackError(!!(value > 100 && unit === '%'));
      setHasInvalidHours(
        !!(value > 60 && !curValue && header === CYBER_CONSTANT.LABEL.ACCESS_KPI),
      );
      setHasTooHighTargetValue(
        !!(curValue && Number(value) > curValue && header === CYBER_CONSTANT.LABEL.ACCESS_KPI),
      );
    } else {
      setHasChanged(true);
      setCurEstimateCheck(e?.target?.checked);
    }
  };

  useEffect(() => {
    if (hasChanged && !hasError) {
      debouncedSubmitData(kpiItem.kpiId, curEstimateCheck, curValue, tarValue);
    }
  }, [hasChanged, curValue, tarValue, curEstimateCheck, hasError]);

  useEffect(() => {
    let closeTimeout;
    if (shouldShowSuccess) {
      closeTimeout = setTimeout(() => {
        setShouldShowSuccess(false);
      }, 3000);
    }
    return () => {
      if (closeTimeout) {
        clearTimeout(closeTimeout);
      }
    };
  }, [shouldShowSuccess]);

  useEffect(() => {
    let closeTimeout;
    if (limitExceeded) {
      closeTimeout = setTimeout(() => {
        setLimitExceeded(false);
      }, 3000);
    }
    return () => {
      if (closeTimeout) {
        clearTimeout(closeTimeout);
      }
    };
  }, [limitExceeded]);

  useEffect(() => {
    let closeTimeout;
    if (wentWrong) {
      closeTimeout = setTimeout(() => {
        setWentWrong(false);
      }, 3000);
    }
    return () => {
      if (closeTimeout) {
        clearTimeout(closeTimeout);
      }
    };
  }, [wentWrong]);

  const toggleFullContext = () => {
    setIsShowingFullContext((value) => !value);

    window.pushEvent?.(
      {
        ...(!isShowingFullContext
          ? CYBER_CONSTANT.ACTIS.CONTEXT_ACCORDION_OPEN
          : CYBER_CONSTANT.ACTIS.CONTEXT_ACCORDION_CLOSE),
        ...actisData,
      },
      Constants.LABELS.ACTIS_KEY_CYBER,
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === '-') {
      e.preventDefault();
      setHasError(true);
    }
  };

  const contextContent = (
    <div className="drawerContextAnimation">
      {kpiItem?.kpiId === 1 && (
        <img className="kpiImage" alt="mttc" src={MTTC} />
      )}
      {kpiItem?.kpiId === 2 && (
        <img className="kpiImage" alt="mttr" src={MTTR} />
      )}
      <Typography className="drawerText">{kpiItem?.text}</Typography>
      <Typography className="drawerTextTitle">{kpiItem?.scope}</Typography>
      <Typography className="drawerText">{kpiItem?.scopeVal}</Typography>
      {kpiItem.definition && (
        <>
          <Typography className="drawerTextTitle">
            {CYBER_CONSTANT.LABEL.DEFINITION}
          </Typography>
          <Typography className="drawerText">
            {kpiItem.definition}
          </Typography>
        </>
      )}
      {kpiItem.clock && (
        <>
          <Typography className="drawerTextTitle">
            {CYBER_CONSTANT.LABEL.CLOCK}
          </Typography>
          <Typography className="drawerText">{kpiItem.clock}</Typography>
        </>
      )}
      {kpiItem.calculation && (
        <>
          <Typography className="drawerTextTitle">
            {CYBER_CONSTANT.LABEL.CALCULATION}
          </Typography>
          <Typography className="drawerText">
            {kpiItem.calculation}
          </Typography>
        </>
      )}
    </div>
  );

  return (
    <div className="drawerBox">
      <div className="drawerTitleDiv">
        <Typography className="drawerTitle">
          {kpiItem?.header}
        </Typography>
        <CloseIcon onClick={handleClose} className="closeIcon-cyber" />
      </div>
      <Typography className="subTitle">
        {kpiItem?.subheader}
      </Typography>
      <Typography className="drawerTargetQsn">
        {kpiItem?.targetQsn}
      </Typography>
      {!isShowingFullContext ? (
        <Button
          variant="ghost"
          onClick={toggleFullContext}
          className="viewMoreContext"
          endIcon={(
            <KeyboardArrowRightIcon
              sx={{
                marginBottom: '-5px',
              }}
            />
          )}
        >
          {CYBER_CONSTANT.LABEL.VIEW_MORE}
        </Button>
      ) : (
        <>
          {contextContent}
          <Button
            variant="ghost"
            onClick={toggleFullContext}
            className="viewMoreContext"
            endIcon={(
              <KeyboardArrowUpIcon
                sx={{
                  marginBottom: '-5px',
                }}
              />
            )}
          >
            {CYBER_CONSTANT.LABEL.VIEW_LESS}
          </Button>
        </>
      )}
      <Grid className="drawerInputs">
        <div>
          <Typography className="inputLabel">
            {CYBER_CONSTANT.LABEL.CURRENT_ACTUAL}
          </Typography>
          <TextField
            variant="outlined"
            className="inputField-cyber"
            min="0"
            onKeyDown={handleKeyDown}
            onChange={inputData(
              CYBER_CONSTANT.LABEL.ISCURRENT,
              kpiItem?.unit,
              kpiItem?.header,
            )}
            value={curValue}
            InputProps={{
              endAdornment: kpiItem?.unit || '',
            }}
          />
          <Grid className="checkBox">
            <Checkbox
              className="bold"
              checked={curEstimateCheck}
              onChange={inputData(CYBER_CONSTANT.LABEL.ISCHECK)}
            />
            <Typography className="bold">
              {CYBER_CONSTANT.LABEL.CHECKBOX_TEXT}
            </Typography>
          </Grid>
        </div>
        <div>
          <Typography className="inputLabel">
            {CYBER_CONSTANT.LABEL.TARGET}
          </Typography>
          <TextField
            variant="outlined"
            className="inputField-cyber"
            min="0"
            onKeyDown={handleKeyDown}
            onChange={inputData(
              CYBER_CONSTANT.LABEL.ISTARGET,
              kpiItem?.unit,
              kpiItem?.header,
            )}
            value={tarValue}
            InputProps={{
              endAdornment: kpiItem?.unit || '',
            }}
          />
        </div>
        <SnackbarComponent
          msg={Constants.ERROR.RANGE_ERROR}
          onClose={() => {}}
          variant={Constants.SNACKBAR.ERROR}
          open={hasError || hasFallbackError}
        />

        <SnackbarComponent
          msg={Constants.ERROR.NONZERO_ERROR}
          onClose={() => {}}
          variant={Constants.SNACKBAR.ERROR}
          open={hasNonZeroError}
        />

        <SnackbarComponent
          msg={CYBER_CONSTANT.LABEL.SAVED}
          onClose={() => {}}
          variant={Constants.SNACKBAR.SUCCESS}
          open={shouldShowSuccess}
        />

        <SnackbarComponent
          msg={Constants.ERROR.HOURS_ERROR}
          onClose={() => {}}
          variant={Constants.SNACKBAR.WARNING}
          open={hasInvalidHours}
        />

        <SnackbarComponent
          msg={Constants.ERROR.TARGET_VALUE_TOO_HIGH_ERROR}
          onClose={() => {}}
          variant={Constants.SNACKBAR.WARNING}
          open={hasTooHighTargetValue}
        />
        <SnackbarComponent
          msg={Constants.ERROR.WENT_WORNG}
          onClose={() => {}}
          variant={Constants.SNACKBAR.ERROR}
          open={wentWrong}
        />
        <SnackbarComponent
          msg={Constants.ERROR.LIMIT_EXCEEDED}
          onClose={() => {}}
          variant={Constants.SNACKBAR.ERROR}
          open={limitExceeded}
        />
      </Grid>
      {isVersionTwoEnabled && (
        <>
          <div className="drawerPartition" />
          <div className="drawerTrend">
            <div className="comparisionDiv">
              <Typography className="comparisionMainHeading">
                <b>{CYBER_CONSTANT.LABEL.PEER_COMPARIONS}</b>
                {(!cohortSelection[CYBER_CONSTANT.LABEL.INDUSTRY_COHORT_KEY]?.length
                  && !cohortSelection[CYBER_CONSTANT.LABEL.REVENUE_COHORT_KEY]?.length
                  && !cohortSelection[CYBER_CONSTANT.LABEL.REGION_COHORT_KEY]?.length)
                  && CYBER_CONSTANT.LABEL.ENTIRE_DATASET}
              </Typography>
              {/* // TODO: Below code in needed for next MVP */}
              {/* {cohortSelection[CYBER_CONSTANT.LABEL.INDUSTRY_COHORT_KEY]?.length > 0 && (
                <div className="internalFiltersDiv">
                  <Typography>
                    <b>{`${CYBER_CONSTANT.LABEL.INDUSTRYCOHORT}:`}</b>
                  </Typography>
                  <Typography>
                    {cohortSelection[CYBER_CONSTANT.LABEL.INDUSTRY_COHORT_KEY].join(', ')}
                  </Typography>
                </div>
              )}
              {cohortSelection[CYBER_CONSTANT.LABEL.REVENUE_COHORT_KEY]?.length > 0 && (
                <div className="internalFiltersDiv">
                  <Typography>
                    <b>{`${CYBER_CONSTANT.LABEL.REVENUECOHORT}`}</b>
                  </Typography>
                  <Typography>
                    {cohortSelection[CYBER_CONSTANT.LABEL.REVENUE_COHORT_KEY].join(', ')}
                  </Typography>
                </div>
              )}
              {cohortSelection[CYBER_CONSTANT.LABEL.REGION_COHORT_KEY]?.length > 0 && (
                <div className="internalFiltersDiv">
                  <Typography>
                    <b>{CYBER_CONSTANT.LABEL.REGIONCOHORT}</b>
                  </Typography>
                  <Typography>
                    {cohortSelection[CYBER_CONSTANT.LABEL.REGION_COHORT_KEY].join(', ')}
                  </Typography>
                </div>
              )} */}
            </div>
            <TrendChart
              unit={kpiItem?.unit}
              orgCurrent={curValue}
              orgTarget={tarValue}
              topCurrent={benchmarkData?.currentBenchmark?.topPeer}
              topTarget={benchmarkData?.targetBenchmark?.topPeer}
              midCurrent={benchmarkData?.currentBenchmark?.medianPeer}
              midTarget={benchmarkData?.targetBenchmark?.medianPeer}
              bottomCurrent={benchmarkData?.currentBenchmark?.bottomPeer}
              bottomTarget={benchmarkData?.targetBenchmark?.bottomPeer}
              currentPercentile={benchmarkData?.currentBenchmark?.percentile}
              targetPercentile={benchmarkData?.targetBenchmark?.percentile}
              targetNSize={benchmarkData?.targetBenchmark?.nsize}
              currentNSize={benchmarkData?.currentBenchmark?.nsize}
              trendDirection={
                kpiItem?.message?.includes('lower')
                  ? CYBER_CONSTANT.TREND_DIRECTION.LOWER
                  : CYBER_CONSTANT.TREND_DIRECTION.HIGHER
              }
              lastUpdatedAt={getDisplayDate(minimalBenchmarkData?.asOfDate)}
              isLoadingBenchmark={isLoadingBenchmark}
              shouldShowCyberLegend={shouldShowCyberLegend}
              minimumValue={benchmarkData?.minimumValue}
              maximumValue={benchmarkData?.maximumValue}
              tickInterval={benchmarkData?.tickInterval}
              curEstimate={curEstimateCheck}
              lastModifiedDt={convertToLastDate(benchmarkData?.lastUpdatedDate)}
              lastModifiedBy={benchmarkData?.lastUpdatedBy}
            />
          </div>
        </>
      )}
      {showResourcesLoader && (
        <div className="resourcesSkeletonContainer">
          <Skeleton variant="rectangular" className="resourceSkeleton" />
          <Skeleton variant="rectangular" className="resourceSkeleton" />
          <Skeleton variant="rectangular" className="resourceSkeleton" />
        </div>
      )}

      <div className="drawerPartition" />
      {(kpiRecommendedResources[kpiItem.kpiId - 1]?.length > 0) && (
        <div className="recommendedCarousalDiv">
          <Typography className="recommendedTitle">
            {CYBER_CONSTANT.LABEL.RECOMMENDED_RESOURCES}
          </Typography>
          <CarousalComponent
            isRR
            data={kpiRecommendedResources[kpiItem.kpiId - 1]}
            actisData={actisData}
            kpiName={kpiItem?.header}
          />
        </div>
      )}
      {(kpiPeerDiscussion?.length > 0) && (
        <div className="recommendedCarousalDiv">
          <Typography className="recommendedTitle">
            {CYBER_CONSTANT.LABEL.PEER_DISCUSSIONS}
          </Typography>
          <CarousalComponent
            isRR
            data={kpiPeerDiscussion}
            actisData={actisData}
            kpiName={kpiItem?.header}
            isPeer
          />
        </div>
      )}
    </div>
  );
}

DrawerComponent.propTypes = {
  kpiItem: PropTypes.instanceOf(Object).isRequired,
  handleClose: PropTypes.func.isRequired,
  handleError: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  benchmarkForKpi: PropTypes.instanceOf(Object).isRequired,
  minimalBenchmarkData: PropTypes.instanceOf(Object),
  actisData: PropTypes.instanceOf(Object),
  isVersionTwoEnabled: PropTypes.bool,
  isLoadingBenchmark: PropTypes.bool,
  shouldShowCyberLegend: PropTypes.bool,
  kpiRecommendedResources: PropTypes.instanceOf(Object),
  showResourcesLoader: PropTypes.bool.isRequired,
  dashboardId: PropTypes.string,
  drawerStatus: PropTypes.bool,
};

DrawerComponent.defaultProps = {
  minimalBenchmarkData: {},
  actisData: {},
  isVersionTwoEnabled: false,
  isLoadingBenchmark: false,
  shouldShowCyberLegend: false,
  kpiRecommendedResources: {},
  dashboardId: '',
  drawerStatus: false,
};

export default DrawerComponent;
